<template>
  <InfuseTable
    :columns="columns"
    :rows="rows"
    :active="(row, id) => active(row, id)"
    :loading="loading"
    :search="search"
    :has-more-items="hasMoreItems"
    :disabled-rows="disabledRows"
    :removable="removable"
    :editable="editable"
    @selected="(value) => (selectedRows = value)"
    @search="(query: string) => emit('search', query)"
    @load="emit('load')"
    @click-row="(row) => emit('clickRow', row)"
    @remove="(row) => emit('remove', row)"
    :remove-permission-id="tenantPermissions.DELETE"
    @edit="(row) => emit('edit', row)"
    :edit-permission-id="tenantPermissions.UPDATE"
    :no-data-title="t('iam.tenants.no_data_title')"
    :no-data-description="t('iam.tenants.no_data_description')"
  >
    <template #actions>
      <InfuseButton class="flex w-64 items-center" size="medium" @click="emit('create')">
        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
        {{ t('iam.tenants.header.add_button') }}
      </InfuseButton>
    </template>

    <template #noResultsActions>
      <InfuseButton class="flex w-64 items-center" size="medium" @click="emit('create')">
        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
        {{ t('iam.tenants.header.add_button') }}
      </InfuseButton>
    </template>

    <template #active="{ data }">
      <font-awesome-icon
        :icon="['fal', data ? 'circle-check' : 'circle-xmark']"
        class="h-5 w-5"
        :class="data ? 'text-success-500' : 'text-error-500'"
      />
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { Tenant, tenantPermissions } from '@infuse/api/iam/tenant';
import { InfuseButton, InfuseTable } from '@infuse/ui/components';
import type { UiTableColumn } from '@infuse/ui/domain';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Emits {
  (e: 'load'): void;
  (e: 'clickRow', row: Tenant): void;
  (e: 'search', query: string): void;
  (e: 'remove', row: Tenant): void;
  (e: 'edit', row: Tenant): void;
  (e: 'create'): void;
}

const emit = defineEmits<Emits>();
const selectedRows = ref<Tenant[]>([]);

interface Props {
  rows: Tenant[];
  active?: (row: Tenant, id?: number) => boolean;
  loading: boolean;
  page: number;
  searchQuery?: string;
  searchEnabled: boolean;
  removable?: boolean;
  editable?: boolean;
  hasMoreItems: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  rows: () => [],
  active: () => false,
  loading: false,
  searchQuery: '',
  searchEnabled: true,
  removable: false,
  editable: false,
  hasMoreItems: true
});

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('iam.tenants.search_placeholder'),
        query: props.searchQuery
      }
);

const disabledRows = computed(() => {
  const row = selectedRows.value[0];
  if (!row) {
    return [];
  }
  return props.rows.filter((tenant) => tenant.id !== row.id);
});

const columns: UiTableColumn[] = [
  {
    name: 'name',
    label: () => t('iam.tenants.name'),
    field: 'name',
    bold: true
  },
  {
    name: 'description',
    label: () => t('iam.tenants.description'),
    field: 'description'
  },
  {
    name: 'active',
    label: () => t('iam.tenants.active'),
    field: 'active'
  }
];
</script>
